import qs from 'query-string'
import {
  useState,
  useEffect,
  useCallback
} from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { useApolloClient } from 'react-apollo'
import { path, pathEq } from 'ramda'

import { useSubscriptionIframe } from '../../shared/hooks/use-subscription-iframe'
import {
  MODALS,
  SIGN_UP_SCREENS,
  SUBSCRIPTION_STATUS
} from '../../../constants'
import {
  paymentCopy,
  planType
} from '../../shared/subscription-constants'
import { useAddPaymentMethodIframe } from '../../shared/hooks/use-add-payment-method-iframe'
import { getModalLocation } from '../../../lib/modal'
import { usePlanChangeFlag } from '../../../hooks/usePlanChange'
import { usePlanDetails } from '../../shared/hooks/use-plan-details'
import { segmentTrackInitiatePurchase } from '../../../segment/segment-track'
import { getGQLErrorCode } from '../../../lib/apollo'

import ACCOUNT_QUERY_WITH_DIR from '../../../../graphql/queries/account-with-directives.gql'
import SUBSCRIPTION_PAYMENT_VIEW_QUERY from '../../../../graphql/queries/subscription-payment-view.gql'
import CREDITCARD_ADD from '../../../../graphql/queries/creditcard-add.gql'

/**
 * Responsible for fetching and providing data to
 * the subscription payment screen.
 */
export const useSubscriptionPayment = () => {
  const [loading, setLoading] = useState(true)
  const [serverData, setServerData] = useState({
    iframeUrl: '',
    details: []
  })

  const location = useLocation()
  const history = useHistory()
  const client = useApolloClient()
  const { getPlanDetailById } = usePlanDetails()

  const { voucher, plan } = qs.parse(location.search)
  const isAnnuallyPlan = plan === planType.ANNUAL
  const isTvodPlan = plan === planType.TVOD
  const isStandardPlan = plan === planType.STANDARD
  const isBasicPlan = plan === planType.BASIC

  const getPlanDetails = usePlanChangeFlag()

  // API functions integrated directly
  const doesUserHaveAnActiveSubscription = async () => {
    try {
      const response = await client.query({
        query: ACCOUNT_QUERY_WITH_DIR,
        fetchPolicy: 'cache-first',
        variables: {
          withCreditcards: true,
          withSubscription: true
        }
      })

      return pathEq(
        ['data', 'account', 'subscription', 'status'],
        SUBSCRIPTION_STATUS.ACTIVE
      )(response)
    } catch (error) {
      const errorCode = getGQLErrorCode(error)
      throw new Error(errorCode)
    }
  }

  const getSubscriptionPaymentDetails = async (subscriptionId, voucherCode, enablePromotion, bundleType) => {
    try {
      const result = await client.query({
        query: SUBSCRIPTION_PAYMENT_VIEW_QUERY,
        variables: {
          subscriptionId,
          voucher: voucherCode,
          promotions: enablePromotion,
          bundleType
        },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true
      })

      return {
        iframe: path(['data', 'subscriptionPaymentView', 'iframe'], result),
        details: path(['data', 'subscriptionPaymentView', 'details'], result)
      }
    } catch (error) {
      const errorCode = getGQLErrorCode(error)
      throw new Error(errorCode)
    }
  }

  const addPaymentMethod = async () => {
    try {
      const res = await client.query({
        query: CREDITCARD_ADD,
        fetchPolicy: 'network-only'
      })
      return path(['data', 'creditcardAdd'], res)
    } catch (error) {
      const errorCode = getGQLErrorCode(error)
      throw new Error(errorCode)
    }
  }

  const onSuccess = useCallback(orderId => {
    history.push({
      ...location,
      pathname: SIGN_UP_SCREENS.CONFIRMATION.path,
      search: qs.stringify({
        ...qs.parse(location.search),
        orderId
      })
    })
  }, [])

  /**
   * Handler for tvod user add payment method successfully
   */
  const onAddCardSuccessHandler = useCallback((status) => {
    if (status === 'success') {
      history.push({
        ...location,
        pathname: SIGN_UP_SCREENS.CONFIRMATION.path,
        search: qs.stringify({
          ...qs.parse(location.search),
          status
        })
      })
    }
  }, [])

  /**
   * Handler for tvod user cancel add payment method
   */
  const onAddCancelCancelHandler = useCallback(() => {
    history.replace(
      getModalLocation(location, MODALS.qsParams.signupTVODCancelAddPayment)
    )
  }, [])

  let iframeRefByPlan
  let promotionState = false
  if (isTvodPlan) {
    const { iframeRef: addPaymentIframeRef } = useAddPaymentMethodIframe(onAddCardSuccessHandler, onAddCancelCancelHandler)
    iframeRefByPlan = addPaymentIframeRef
  } else {
    const { iframeRef: subscriptionRef, promotionRejected } = useSubscriptionIframe(onSuccess)
    iframeRefByPlan = subscriptionRef
    promotionState = promotionRejected
  }

  useEffect(() => {
    const loadServerData = async () => {
      try {
        const hasSubscription = await doesUserHaveAnActiveSubscription()

        if (hasSubscription) {
          onSuccess(200)
          return
        }

        let data
        if (isTvodPlan) {
          const addPaymentIframe = await addPaymentMethod()
          data = {
            iframe: addPaymentIframe,
            details: []
          }
        } else {
          let subscriptionId = ''
          if (isAnnuallyPlan) {
            subscriptionId = getPlanDetails?.annual.id
          } else if (isStandardPlan) {
            subscriptionId = getPlanDetails?.standard.id
          } else if (isBasicPlan) {
            subscriptionId = getPlanDetails?.basic.id
          }

          data = await getSubscriptionPaymentDetails(
            subscriptionId,
            voucher || null,
            true,
            null
          )

          // Add segment data analytics for initiating payment
          const selectedPlan = getPlanDetailById(subscriptionId)
          const sku = selectedPlan?.sku
          const paymentDue = data?.details[0]?.value
          const payment = Number(data?.details[1]?.value?.replace('$', ''))
          segmentTrackInitiatePurchase({
            ...selectedPlan,
            sku,
            paymentDue,
            payment,
            voucher
          })
        }

        setServerData(data)
        setLoading(false)
      } catch (error) {
        // Redirect to homepage
        history.push('/')
      }
    }

    loadServerData()
  }, [])

  const getPaymentCopy = () => {
    if (voucher || (plan === planType.STANDARD)) return paymentCopy.standard
    if (isBasicPlan) return paymentCopy.basic
    if (isAnnuallyPlan) return paymentCopy.annual
    if (plan === planType.TVOD) return paymentCopy.tvod
    return paymentCopy.standard
  }

  const copy = promotionState || getPaymentCopy()

  let staticDetail = null
  if (isAnnuallyPlan) {
    staticDetail = getPlanDetails?.annual.staticDetail
  } else if (isStandardPlan) {
    staticDetail = getPlanDetails?.standard.staticDetail
  } else if (isBasicPlan) {
    staticDetail = getPlanDetails?.basic.staticDetail
  }

  const nonTVODPlanDetails = [
    staticDetail,
    ...serverData.details
  ]
  const detail = isTvodPlan ? serverData.details : nonTVODPlanDetails

  return {
    copy,
    detail,
    loading,
    iframeRef: iframeRefByPlan,
    promotionRejected: promotionState,
    iframeUrl: serverData.iframe,
    showVoucherMessage: Boolean(voucher),
    isTvodPlan,
    isAnnuallyPlan,
    isStandardPlan
  }
}
